'use client';

import React, { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useModal } from 'components/shared';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import {
  RECOVERY_PASS_THUMBS,
  recoveryPassThumbSrc,
} from './recoveryPageAssets';
import { DiagnosticLearnMoreModal } from './DiagnosticLearnMoreModal';
import { PerformanceLabModalBodyScrollLock } from './PerformanceLabModalBodyScrollLock';
import { RECOVERY_DATA } from './CollapsibleAccordionSection/data';
import type { CollapsibleItem } from './CollapsibleAccordionSection/data';
import { PLCTA } from './PLCTA';
import { useHandleBookNow } from './hooks';

import { getPerformanceLabLenis } from 'utils/performanceLabLenisRegistry';
import {
  PlContentShell,
  PlSectionRoot,
  SectionContent,
} from './sectionLayout';
import styles from './RecoveryPassesSection.module.css';

const PASS_IDS = [
  'infrared-sauna',
  'cold-plunge',
  'decompression',
  'massage-guns',
  'powerplate',
  'compression',
  'localized-hot-cold-therapy',
] as const;

const PASS_TITLES: Record<string, { key: string; fallback: string }> = {
  'infrared-sauna': {
    key: 'recoveryPage.passes.items.sauna.title',
    fallback: 'Sauna',
  },
  'cold-plunge': {
    key: 'recoveryPage.passes.items.coldPlunge.title',
    fallback: 'Cold Plunge',
  },
  decompression: {
    key: 'recoveryPage.passes.items.inversions.title',
    fallback: 'Inversion Table',
  },
  'massage-guns': {
    key: 'recoveryPage.passes.items.massageGuns.title',
    fallback: 'Massage Guns',
  },
  powerplate: {
    key: 'recoveryPage.passes.items.powerplate.title',
    fallback: 'Powerplate',
  },
  compression: {
    key: 'recoveryPage.passes.items.compression.title',
    fallback: 'Compression Therapy',
  },
  'localized-hot-cold-therapy': {
    key: 'recoveryPage.passes.items.localizedHotCold.title',
    fallback: 'Localized Hot & Cold Therapy',
  },
};

const ITEMS_BY_ID = new Map(RECOVERY_DATA.items.map((item) => [item.id, item]));
const PASSES: CollapsibleItem[] = PASS_IDS.map((id) => ITEMS_BY_ID.get(id)).filter(
  (item): item is CollapsibleItem => Boolean(item),
);

const handlePassCardKeyDown = (
  event: React.KeyboardEvent,
  hasModal: boolean,
  onActivate: () => void,
) => {
  if (!hasModal) return;
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    onActivate();
  }
};

type LenisScrollTarget = {
  scrollTo: (
    target: Element | string | number,
    options?: { offset?: number },
  ) => void;
};

export const RecoveryPassesSection: React.FC = () => {
  const t = useTranslations('performance-lab');
  const modal = useModal();
  const handleBookNow = useHandleBookNow({ category: 'recovery' });

  const handleViewPlans = useCallback(() => {
    const target = document.getElementById('recovery-plans-section');
    if (!target) return;

    const lenis = getPerformanceLabLenis() as LenisScrollTarget | null;
    if (lenis?.scrollTo) {
      lenis.scrollTo(target, { offset: -120 });
      return;
    }

    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const openLearnMore = useCallback(
    (item: CollapsibleItem, displayTitle: string) => {
      if (!item.modalData) return;

      modal.set(
        <PerformanceLabModalBodyScrollLock>
          <DiagnosticLearnMoreModal
            title={displayTitle}
            modalData={item.modalData}
            onClose={() => {
              modal.hide();
              modal.clear();
            }}
            onCtaClick={() => {
              modal.hide();
              modal.clear();
              handleBookNow();
            }}
          />
        </PerformanceLabModalBodyScrollLock>,
      );
      modal.setFullView();
      modal.setCloseBtnHidden(true);
      modal.setTop();
      modal.setAlignRight();
      modal.setPortal(true);
      modal.show();
    },
    [handleBookNow, modal],
  );

  const cards = useMemo(
    () =>
      PASSES.map((item, index) => {
        const titleConfig = PASS_TITLES[item.id];
        const title = titleConfig
          ? t.has(titleConfig.key)
            ? t(titleConfig.key)
            : titleConfig.fallback
          : item.title;
        const stepLabel = String(index + 1).padStart(2, '0');

        const passThumbFilename = RECOVERY_PASS_THUMBS[item.id];
        const thumbSrc = passThumbFilename
          ? getCompressedAssetUrl(`/${recoveryPassThumbSrc(passThumbFilename)}`)
          : getCompressedAssetUrl(`/${item.panelImageSrc}`);

        const hasModal = Boolean(item.modalData);
        const learnMoreAriaLabel = t('recoveryPage.passes.learnMoreAria', {
          pass: title,
        });

        return (
          <li
            key={item.id}
            className={styles.card}
            data-clickable={hasModal ? 'true' : undefined}
            role={hasModal ? 'button' : undefined}
            tabIndex={hasModal ? 0 : undefined}
            aria-label={hasModal ? learnMoreAriaLabel : undefined}
            onClick={
              hasModal ? () => openLearnMore(item, title) : undefined
            }
            onKeyDown={(event) =>
              handlePassCardKeyDown(event, hasModal, () =>
                openLearnMore(item, title),
              )
            }
          >
            <img
              className={styles.thumb}
              src={thumbSrc}
              alt={`${title}, Performance Lab`}
              loading="lazy"
            />
            <div className={styles.cardBody}>
              <span className={styles.stepNumber} aria-hidden="true">{stepLabel}</span>
              <div className={styles.textGroup}>
                <h3 className={styles.cardTitle}>{title}</h3>
                <p className={styles.cardDescription}>{item.description}</p>
              </div>
              {hasModal && (
                <button
                  className={styles.learnMoreButton}
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    openLearnMore(item, title);
                  }}
                  aria-label={learnMoreAriaLabel}
                >
                  {t('recoveryPage.passes.learnMore')}
                </button>
              )}
            </div>
          </li>
        );
      }),
    [openLearnMore, t],
  );

  return (
    <PlSectionRoot className={styles.darkSection} aria-label={t('recoveryPage.passes.aria')}>
      <SectionContent>
        <PlContentShell className={styles.shell}>
          <div className={styles.layout}>
            <div className={styles.headerColumn}>
              <div className={styles.stickyHeader}>
                <h2 className={styles.title}>{t('recoveryPage.passes.title')}</h2>
                <p className={styles.subtitle}>
                  {t('recoveryPage.passes.subtitle')}
                </p>
                <PLCTA
                  className={styles.viewPlansCTA}
                  $plVariant="white"
                  type="button"
                  onClick={handleViewPlans}
                  aria-label={t('recoveryPage.passes.viewPlansCtaAria')}
                  data-buttontype="performance_lab_recovery_view_plans"
                >
                  {t('recoveryPage.passes.viewPlansCta')}
                </PLCTA>
              </div>
            </div>

            <ol className={styles.cards}>{cards}</ol>
          </div>
        </PlContentShell>
      </SectionContent>
    </PlSectionRoot>
  );
};
